import React from "react";
import { cn } from "@/lib/utils";

type PageWithRailLayoutProps = {
    main: React.ReactNode;
    rail?: React.ReactNode;
    className?: string;
    innerClassName?: string;
    gridClassName?: string;
    mainClassName?: string;
    railClassName?: string;
    dataPlaywrightId?: string;
    /**
     * Put the rail FIRST in DOM order and place it right by grid, instead of second-and-desktop-only.
     *
     * The default shape treats the rail as a desktop passenger: `main` leads the document, the rail
     * follows it and is `hidden` below `lg`. That is right when the rail is supplementary (the group
     * page's related-groups column), and wrong when the rail carries the page's identity — the `<h1>`,
     * the avatar, the actions. A profile's heading cannot land after the entire feed in the
     * accessibility tree, and it cannot vanish on mobile.
     *
     * So `railFirst` changes two things and nothing else:
     * - the rail is emitted first and stays visible at every breakpoint, so on mobile it simply renders
     *   where it sits — a compact identity card above the controls and the feed;
     * - at `lg` the two cells get explicit `col-start`/`row-start` assignments, which puts main on the
     *   left and the rail on the right **visually** while DOM order stays rail-first.
     *
     * One card placed by grid, rather than a mobile twin under `lg:hidden` and a desktop twin under
     * `hidden lg:block`: two components would mean two `<h1>`s in the document, a breakpoint-dependent
     * accessibility tree, and two files that have to keep saying the same thing about the same person.
     * Design: 2026-08-06-user-profile-rail-layout-and-achievements-showcase-design.md §2.2.
     */
    railFirst?: boolean;
    /**
     * Below `lg`, place the rail cell AFTER `main` — visually only, by grid `order`.
     *
     * `railFirst` deliberately keeps the rail visible on mobile, where it "simply renders where it
     * sits". That is a fallback of the desktop decision, not a mobile one, and on the dashboard it
     * costs the whole first screenful: the identity card measured ~570-625 CSS px on a 390-412px
     * phone, so the section named in the URL began ~90% of the way down the viewport and the first
     * row's actions fell below the fold. On every one of the six sections.
     *
     * Order, never DOM order: the `<h1>` must stay first in the accessibility tree (§2.2's whole
     * argument against a mobile twin under `lg:hidden`), so this moves the painted box and leaves
     * the document alone. Above `lg` it is inert — the explicit `col-start`/`row-start` assignments
     * already place both cells.
     *
     * Opt-in rather than folded into `railFirst`, because it is only right where the viewer already
     * knows whose page this is. A visitor landing on a public profile is there for the identity;
     * the owner opening their own dashboard is not.
     */
    railBelowMainOnMobile?: boolean;
    /**
     * Keep the rail rendered below `lg`, where the default shape drops it entirely.
     *
     * The default rail cell is `hidden … lg:block`, which is right when the rail is genuinely
     * supplementary — Home's discovery column, the group page's related-groups column — and wrong
     * when it carries an affordance that exists nowhere else on the page. On `/groups` the only
     * create-a-group control lived in the rail, so a phone had **no way to create a group at all**
     * (`DISCOVERY-11`, `EMPTY-STATES-33`); in search mode the hand-built mobile mirror of the rail's
     * other half was suppressed too, so both affordances vanished together.
     *
     * This is deliberately NOT `railFirst`, and not `railFirst` + `railBelowMainOnMobile`. Both of
     * those put the rail FIRST in DOM order, which is correct only when the rail carries the page's
     * identity (the `<h1>`, the avatar) — on the directory the `<h1>` is in `main`, so rail-first
     * would put a promo card ahead of the page heading in the accessibility tree. It is also not a
     * `lg:hidden` twin inside `main`: two copies of one `data-playwright-id` fail strict mode on both
     * Playwright projects with a message about strictness that says nothing about layout
     * (`AGENTS/testing-and-lanes.md`).
     *
     * So it changes exactly one thing: the rail cell stops being hidden below `lg`. DOM order is
     * untouched, and below `lg` the grid is one column — so the rail simply paints AFTER the main
     * column, which is where the intent to start your own group actually peaks: after scrolling a
     * directory and concluding none of it fits.
     *
     * Inert when `railFirst` is set, which already keeps the rail visible at every breakpoint.
     */
    railOnMobile?: boolean;
    /**
     * At `lg` and above, let the rail cell stick to the top of the viewport instead of scrolling away.
     *
     * Every rail on a paginated surface has the same shape: a card a few hundred pixels tall in a
     * column 22-24rem wide, next to a list with no end. Once the card scrolls past, that column is
     * blank for the whole remaining scroll — measured on the dashboard at ~80% of `/arguments/authored`,
     * ~91% of `/series` (`DASHBOARD-20`) — while the list it sits beside is capped narrower than it
     * needs to be. Sticking the cell is the smaller of the two available answers; the other is letting
     * `main` reclaim the container once the rail ends, which is a per-section layout override rather
     * than a shared-shell change.
     *
     * Three classes, and all three are load-bearing:
     * - `lg:self-start` — a grid item stretches to the row height by default, and a sticky element that
     *   already fills its containing block has nothing to stick within, so without this the prop is a
     *   silent no-op. It is set here rather than left to the caller's `gridClassName` precisely because
     *   the failure is invisible.
     * - `lg:max-h-[calc(100vh-2rem)]` with `lg:overflow-y-auto` — the constraint `DASHBOARD-20` names:
     *   a rail card taller than a short viewport must scroll inside itself, or its lower half becomes
     *   unreachable at every scroll position rather than only at the top.
     * - `no-scrollbar` — matches the four rails that already do this by hand (`GroupRail`,
     *   `HomeDiscoveryRail`, `GroupsDirectoryPage`, `DiscussionPage`), so a sticky rail does not
     *   introduce a second scrollbar into the page's visual language.
     *
     * Opt-in, because a rail that is shorter than the viewport on every real page gains nothing from
     * it, and because below `lg` the grid is one column where sticky would pin a card over the list.
     */
    railSticky?: boolean;
};

export function PageWithRailLayout({
    main,
    rail,
    className,
    innerClassName,
    gridClassName,
    mainClassName,
    railClassName,
    dataPlaywrightId,
    railFirst = false,
    railBelowMainOnMobile = false,
    railOnMobile = false,
    railSticky = false,
}: PageWithRailLayoutProps) {
    const reorderOnMobile = railFirst && railBelowMainOnMobile;

    const mainCell = (
        <main
            className={cn(
                "min-w-0",
                railFirst && "lg:col-start-1 lg:row-start-1",
                reorderOnMobile && "order-1 lg:order-none",
                mainClassName,
            )}
        >
            {main}
        </main>
    );

    const railCell = rail ? (
        <div
            className={cn(
                railFirst
                    ? "min-w-0 lg:col-start-2 lg:row-start-1"
                    : (railOnMobile ? "min-w-0" : "hidden min-w-0 lg:block"),
                reorderOnMobile && "order-2 lg:order-none",
                railSticky && "lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto no-scrollbar",
                railClassName,
            )}
            // Stable hook for the layout's own guard spec: the rail cell's visibility rule is shared
            // by five surfaces, so the four that did not ask for a change have to be able to prove
            // they did not get one. See PageWithRailLayout.spec.tsx.
            data-rail-cell="true"
        >
            {rail}
        </div>
    ) : null;

    return (
        <div className={cn("w-full px-4 pb-6 sm:px-6 lg:px-8", className)} data-playwright-id={dataPlaywrightId}>
            <div className={cn("mx-auto w-full max-w-7xl py-2", innerClassName)}>
                <div className={cn("grid w-full gap-6 lg:grid-cols-[minmax(0,1fr)_22rem] xl:grid-cols-[minmax(0,1fr)_24rem]", gridClassName)}>
                    {railFirst ? (
                        <>
                            {railCell}
                            {mainCell}
                        </>
                    ) : (
                        <>
                            {mainCell}
                            {railCell}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
